'use client'

import { Card, CardContent, CardHeader, SkeletonText } from '@sushiswap/ui'
import { type FC, useMemo } from 'react'
import { KATANA_CHAIN_ID } from 'src/config'
import { useClaimableRewards } from 'src/lib/hooks/react-query'
import { useConcentratedLiquidityPositions } from 'src/lib/wagmi/hooks/positions/hooks/useConcentratedLiquidityPositions'
import { formatUSD } from 'sushi'
import { isMerklChainId, isSushiSwapV3ChainId } from 'sushi/evm'
import { useConnection } from 'wagmi'

const V3_CHAIN_IDS = isSushiSwapV3ChainId(KATANA_CHAIN_ID)
  ? ([KATANA_CHAIN_ID] as const)
  : ([] as const)

const MERKL_CHAIN_IDS = isMerklChainId(KATANA_CHAIN_ID) ? [KATANA_CHAIN_ID] : []

export const Hero: FC = () => {
  const { address, isConnected } = useConnection()

  const {
    data: positionsData,
    isError: isPositionsError,
    isInitialLoading: isPositionsLoading,
  } = useConcentratedLiquidityPositions({
    account: address,
    chainIds: V3_CHAIN_IDS,
  })
  const totalFeesUSD = useMemo(() => {
    if (!isConnected || isPositionsError) return 0
    if (isPositionsLoading || !positionsData) return undefined
    return positionsData.reduce(
      (accum, { position: { unclaimedUSD } }) => accum + unclaimedUSD,
      0,
    )
  }, [positionsData, isConnected, isPositionsError, isPositionsLoading])

  const {
    data: rewardsData,
    isError: isRewardsError,
    isLoading: isRewardsLoading,
  } = useClaimableRewards({
    account: address,
    chainIds: MERKL_CHAIN_IDS,
  })
  const totalRewardsUSD = useMemo(() => {
    if (!isConnected || isRewardsError) return 0
    if (isRewardsLoading || !rewardsData) return undefined
    return Object.values(rewardsData).reduce(
      (accum, { totalRewardsUSD }) => accum + totalRewardsUSD,
      0,
    )
  }, [rewardsData, isConnected, isRewardsError, isRewardsLoading])

  return (
    <section className="flex flex-col gap-6">
      <span className="text-5xl font-bold">Claim</span>
      <span className="text-xl max-w-[720px] text-muted-foreground">
        Instead of claiming fees and rewards per V3 liquidity position, press
        claim to claim your fees or rewards across all your V3 liquidity
        positions in one go.
      </span>
      <div className="flex justify-between flex-wrap gap-5">
        <Card className="flex-1">
          <CardHeader className="!p-5 !pb-3">
            <span className="font-medium text-sm text-muted-foreground">
              Claimable Fees
            </span>
          </CardHeader>
          <CardContent>
            {typeof totalFeesUSD === 'number' ? (
              <span className="font-bold text-lg">
                {formatUSD(totalFeesUSD)}
              </span>
            ) : (
              <span className="w-24">
                <SkeletonText fontSize="lg" />
              </span>
            )}
          </CardContent>
        </Card>
        <Card className="flex-1">
          <CardHeader className="!p-5 !pb-3">
            <span className="font-medium text-sm text-muted-foreground">
              Claimable Rewards
            </span>
          </CardHeader>
          <CardContent>
            {typeof totalRewardsUSD === 'number' ? (
              <span className="font-bold text-lg">
                {formatUSD(totalRewardsUSD)}
              </span>
            ) : (
              <span className="w-24">
                <SkeletonText fontSize="lg" />
              </span>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  )
}
